import {
  Receipt,
  Client,
  CompanyDetails,
  Product,
  Category,
} from '../types/receipt';
import toast from 'react-hot-toast';
import { encryptData, decryptData, isEncryptionAvailable } from './encryption';
import { getCurrentUserId } from '../firebase';
import { kvGet, kvSet, kvRemove, StorageQuotaError } from './idbKeyValStore';
import { logger } from './logger';
import { sortReceiptsInPlace } from './receiptSorting';
import { SyncableEntity } from '../types/common';

// Types for offline operations queue
export interface PendingOperation {
  id: string;
  type: 'CREATE_CLIENT' | 'CREATE_RECEIPT' | 'UPDATE_CLIENT' | 'DELETE_CLIENT';
  data: SyncableEntity | PendingClient | PendingReceipt;
  timestamp: number;
  retryCount: number;
  // When this operation was first refused by security rules. A denial is
  // retried for free for a while — a rules deploy or an unpropagated claim
  // clears on its own — and only starts spending the retry budget once that
  // window closes. Absent until the first such refusal.
  firstDeniedAt?: number;
}

export interface PendingClient extends Omit<Client, 'id'> {
  tempId: string;
}

export interface PendingReceipt extends Omit<Receipt, 'id'> {
  tempId: string;
}

// An operation that exhausted its sync attempts. It is NEVER discarded
// automatically — the user's offline work stays recoverable until they
// explicitly retry or delete it from the Dane Offline page.
export interface FailedOperation extends PendingOperation {
  failedAt: number;
  lastError: string;
}

const STORAGE_KEYS = {
  CLIENTS: 'offline_clients',
  RECEIPTS: 'offline_receipts',
  COMPANY_DETAILS: 'offline_company_details',
  PRODUCTS: 'offline_products',
  CATEGORIES: 'offline_categories',
  PENDING_OPERATIONS: 'offline_pending_operations',
  FAILED_OPERATIONS: 'offline_failed_operations',
  LAST_SYNC: 'offline_last_sync',
} as const;

// Data blobs live in IndexedDB; LAST_SYNC stays in localStorage so the
// synchronous getLastSyncTime/isCacheFresh API keeps working
const DATA_KEYS: string[] = [
  STORAGE_KEYS.CLIENTS,
  STORAGE_KEYS.RECEIPTS,
  STORAGE_KEYS.COMPANY_DETAILS,
  STORAGE_KEYS.PRODUCTS,
  STORAGE_KEYS.CATEGORIES,
  STORAGE_KEYS.PENDING_OPERATIONS,
  STORAGE_KEYS.FAILED_OPERATIONS,
];

// Get current user ID safely
const getUserIdSafe = (): string | null => {
  try {
    return getCurrentUserId();
  } catch {
    return null;
  }
};

// One-time move of legacy localStorage payloads into IndexedDB. Raw strings
// are copied as-is, so already-encrypted data stays encrypted.
let migrationPromise: Promise<void> | null = null;

const migrateLegacyLocalStorage = (): Promise<void> => {
  if (!migrationPromise) {
    migrationPromise = (async () => {
      for (const key of DATA_KEYS) {
        try {
          const legacy = localStorage.getItem(key);
          if (legacy === null) continue;

          const existing = await kvGet(key);
          if (existing === null) {
            await kvSet(key, legacy);
          }
          localStorage.removeItem(key);
        } catch (error) {
          // Keep the localStorage copy if it cannot be moved — the data
          // must not be lost
          logger.warn(
            `Failed to migrate offline data key to IndexedDB: ${key}`,
            undefined,
            {
              component: 'OfflineStorage',
              operation: 'migrateLegacyLocalStorage',
              extra: {
                migrationKey: key,
                error: error instanceof Error ? error.message : String(error),
              },
            }
          );
        }
      }
    })();
  }
  return migrationPromise;
};

// Quota failures must be loud: the user needs to know their offline data
// is NOT being saved. Toasts are throttled, the error always propagates.
const QUOTA_TOAST_THROTTLE_MS = 30_000;
let lastQuotaToastAt = 0;

const notifyQuotaExceeded = (key: string): void => {
  logger.critical(
    'Offline storage quota exceeded — data was not persisted',
    undefined,
    {
      component: 'OfflineStorage',
      operation: 'setItem',
      extra: { storageKey: key },
    }
  );

  const now = Date.now();
  if (now - lastQuotaToastAt >= QUOTA_TOAST_THROTTLE_MS) {
    lastQuotaToastAt = now;
    toast.error(
      'Pamięć offline jest pełna — dane nie zostały zapisane. ' +
        'Wyczyść dane offline w ustawieniach i spróbuj ponownie.',
      { duration: 8000 }
    );
  }
};

// Secure storage wrapper (IndexedDB-backed)
const secureStorage = {
  setItem: async (key: string, data: unknown): Promise<void> => {
    await migrateLegacyLocalStorage();

    const userId = getUserIdSafe();
    // Fallback to plain JSON if no user or encryption unavailable
    const payload =
      !userId || !isEncryptionAvailable()
        ? JSON.stringify(data)
        : await encryptData(data, userId);

    try {
      await kvSet(key, payload);
    } catch (error) {
      if (error instanceof StorageQuotaError) {
        notifyQuotaExceeded(key);
      }
      throw error;
    }
  },

  getItem: async <T>(key: string): Promise<T | null> => {
    await migrateLegacyLocalStorage();

    const userId = getUserIdSafe();
    let stored = await kvGet(key);

    if (stored === null) {
      // A failed migration leaves the legacy copy in localStorage (it is
      // only removed after a successful move) — fall back so the data
      // stays readable; the migration retries on next load
      stored = localStorage.getItem(key);
    }

    if (!stored) return null;

    if (!userId || !isEncryptionAvailable()) {
      // Fallback to regular parsing
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }

    return await decryptData<T>(stored, userId);
  },
};

// Cache management
export const offlineStorage = {
  // Clients
  cacheClients: async (clients: Client[]): Promise<void> => {
    // Deduplicate clients by ID and remove any temp entries that might have real counterparts
    const deduplicatedClients = deduplicateAndCleanEntries(
      clients,
      'temp_client_'
    );
    await secureStorage.setItem(STORAGE_KEYS.CLIENTS, deduplicatedClients);
    localStorage.setItem(STORAGE_KEYS.LAST_SYNC, Date.now().toString());
  },

  // NEW: Merge clients with existing cache instead of overwriting
  mergeClients: async (newClients: Client[]): Promise<void> => {
    const existingClients = await offlineStorage.getCachedClients();
    const allClients = [...existingClients, ...newClients];
    const deduplicatedClients = deduplicateAndCleanEntries(
      allClients,
      'temp_client_'
    );
    await secureStorage.setItem(STORAGE_KEYS.CLIENTS, deduplicatedClients);
    localStorage.setItem(STORAGE_KEYS.LAST_SYNC, Date.now().toString());
  },

  getCachedClients: async (): Promise<Client[]> => {
    const clients = await secureStorage.getItem<Client[]>(STORAGE_KEYS.CLIENTS);
    return clients || [];
  },

  // Receipts
  cacheReceipts: async (receipts: Receipt[]): Promise<void> => {
    // Deduplicate receipts by ID and remove any temp entries that might have real counterparts
    const deduplicatedReceipts = deduplicateAndCleanEntries(
      receipts,
      'temp_receipt_'
    );

    // Only cache recent receipts to avoid storage bloat
    const recentReceipts = deduplicatedReceipts
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 100); // Cache only the 100 most recent receipts

    await secureStorage.setItem(STORAGE_KEYS.RECEIPTS, recentReceipts);
    localStorage.setItem(STORAGE_KEYS.LAST_SYNC, Date.now().toString());
  },

  // NEW: Merge receipts with existing cache instead of overwriting
  mergeReceipts: async (newReceipts: Receipt[]): Promise<void> => {
    const existingReceipts = await offlineStorage.getCachedReceipts();
    const allReceipts = [...existingReceipts, ...newReceipts];
    const deduplicatedReceipts = deduplicateAndCleanEntries(
      allReceipts,
      'temp_receipt_'
    );

    // Only cache recent receipts to avoid storage bloat
    sortReceiptsInPlace(deduplicatedReceipts);
    const recentReceipts = deduplicatedReceipts.slice(0, 100); // Cache only the 100 most recent receipts

    await secureStorage.setItem(STORAGE_KEYS.RECEIPTS, recentReceipts);
    localStorage.setItem(STORAGE_KEYS.LAST_SYNC, Date.now().toString());
  },

  getCachedReceipts: async (): Promise<Receipt[]> => {
    const receipts = await secureStorage.getItem<Receipt[]>(
      STORAGE_KEYS.RECEIPTS
    );
    if (!receipts) return [];

    // Convert date strings back to Date objects
    return receipts.map(receipt => ({
      ...receipt,
      date: new Date(receipt.date),
    }));
  },

  /**
   * Drop one receipt from the cache.
   *
   * Deleting a receipt has to say so explicitly, because the only thing that
   * refreshes this cache afterwards is mergeReceipts — and a merge can add an
   * entry but never remove one. Receipts are fetched a page at a time, so
   * replacing the cache wholesale would throw away every other page; merging
   * is the right call for the read path and is exactly why the delete path
   * has to do this itself.
   *
   * Left undone, the deleted receipt lingered here and
   * checkDuplicateReceiptNumber kept reporting its number as taken — so the
   * number could not be reused until the entry aged out of the hundred most
   * recent, or the user cleared the site data. The server side was correct
   * the whole time, which is what made it confusing.
   */
  removeCachedReceipt: async (receiptId: string): Promise<void> => {
    const receipts = await offlineStorage.getCachedReceipts();
    const remaining = receipts.filter(receipt => receipt.id !== receiptId);
    if (remaining.length === receipts.length) return;

    await secureStorage.setItem(STORAGE_KEYS.RECEIPTS, remaining);
  },

  /**
   * Replace one receipt in the cache.
   *
   * Editing has to say so explicitly for the same reason deleting does, and
   * the reason is worth stating precisely: mergeReceipts cannot correct a
   * stale entry. It concatenates the existing cache ahead of the newly
   * fetched page, and deduplicateAndCleanEntries keeps the FIRST copy it sees
   * of an id — so the stale copy wins over the fresh one, however many times
   * the list is refetched. Only a wholesale cacheReceipts replaces it, and
   * the only thing that does that is OfflineDataHandler on app load.
   *
   * The case that matters is renumbering. The edited receipt keeps its old
   * number here, checkDuplicateReceiptNumber finds it, and the number the
   * user just freed is reported as already taken — on a device where the
   * server has been correct the whole time. Until a reload.
   */
  updateCachedReceipt: async (receipt: Receipt): Promise<void> => {
    const receipts = await offlineStorage.getCachedReceipts();
    const index = receipts.findIndex(entry => entry.id === receipt.id);
    // Absent means there is nothing stale to correct. The cache holds only
    // the hundred most recent receipts, and one it never had cannot lie
    // about a number.
    if (index === -1) return;

    receipts[index] = receipt;
    await secureStorage.setItem(STORAGE_KEYS.RECEIPTS, receipts);
  },

  // Company Details
  cacheCompanyDetails: async (details: CompanyDetails): Promise<void> => {
    await secureStorage.setItem(STORAGE_KEYS.COMPANY_DETAILS, details);
    localStorage.setItem(STORAGE_KEYS.LAST_SYNC, Date.now().toString());
  },

  getCachedCompanyDetails: async (): Promise<CompanyDetails | null> => {
    return await secureStorage.getItem<CompanyDetails>(
      STORAGE_KEYS.COMPANY_DETAILS
    );
  },

  // Products
  cacheProducts: async (products: Product[]): Promise<void> => {
    await secureStorage.setItem(STORAGE_KEYS.PRODUCTS, products);
    localStorage.setItem(STORAGE_KEYS.LAST_SYNC, Date.now().toString());
  },

  getCachedProducts: async (): Promise<Product[]> => {
    const products = await secureStorage.getItem<Product[]>(
      STORAGE_KEYS.PRODUCTS
    );
    return products || [];
  },

  // Categories
  cacheCategories: async (categories: Category[]): Promise<void> => {
    await secureStorage.setItem(STORAGE_KEYS.CATEGORIES, categories);
    localStorage.setItem(STORAGE_KEYS.LAST_SYNC, Date.now().toString());
  },

  getCachedCategories: async (): Promise<Category[]> => {
    const categories = await secureStorage.getItem<Category[]>(
      STORAGE_KEYS.CATEGORIES
    );
    return categories || [];
  },

  // Pending Operations Queue
  addPendingOperation: async (
    operation: Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'>
  ): Promise<string> => {
    const operations = await offlineStorage.getPendingOperations();
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    const newOperation: PendingOperation = {
      ...operation,
      id,
      timestamp: Date.now(),
      retryCount: 0,
    };

    operations.push(newOperation);
    await secureStorage.setItem(STORAGE_KEYS.PENDING_OPERATIONS, operations);

    return id;
  },

  getPendingOperations: async (): Promise<PendingOperation[]> => {
    const operations = await secureStorage.getItem<PendingOperation[]>(
      STORAGE_KEYS.PENDING_OPERATIONS
    );
    return operations || [];
  },

  removePendingOperation: async (operationId: string): Promise<void> => {
    const operations = await offlineStorage.getPendingOperations();
    const filtered = operations.filter(op => op.id !== operationId);
    await secureStorage.setItem(STORAGE_KEYS.PENDING_OPERATIONS, filtered);
  },

  updatePendingOperation: async (
    operationId: string,
    updates: Partial<PendingOperation>
  ): Promise<void> => {
    const operations = await offlineStorage.getPendingOperations();
    const index = operations.findIndex(op => op.id === operationId);
    if (index !== -1) {
      operations[index] = { ...operations[index], ...updates };
      await secureStorage.setItem(STORAGE_KEYS.PENDING_OPERATIONS, operations);
    }
  },

  // Failed (quarantined) operations queue
  getFailedOperations: async (): Promise<FailedOperation[]> => {
    const operations = await secureStorage.getItem<FailedOperation[]>(
      STORAGE_KEYS.FAILED_OPERATIONS
    );
    return operations || [];
  },

  /**
   * Move an operation out of the pending queue into quarantine. This is the
   * only exit from the pending queue that is not a successful sync — nothing
   * here deletes the user's work.
   *
   * The failed list is written BEFORE the pending entry is removed: if the
   * process dies between the two writes the operation is duplicated rather
   * than lost, and restoreFailedOperation() dedupes on the way back.
   */
  quarantinePendingOperation: async (
    operationId: string,
    lastError: string
  ): Promise<void> => {
    const operations = await offlineStorage.getPendingOperations();
    const operation = operations.find(op => op.id === operationId);
    if (!operation) return;

    const failed = await offlineStorage.getFailedOperations();
    if (!failed.some(op => op.id === operationId)) {
      failed.push({ ...operation, failedAt: Date.now(), lastError });
      await secureStorage.setItem(STORAGE_KEYS.FAILED_OPERATIONS, failed);
    }

    await offlineStorage.removePendingOperation(operationId);

    logger.warn(
      `Sync operation quarantined after repeated failures: ${operation.type}`,
      undefined,
      {
        component: 'OfflineStorage',
        operation: 'quarantinePendingOperation',
        extra: { operationId, operationType: operation.type, lastError },
      }
    );
  },

  /**
   * Put a quarantined operation back in the pending queue with a fresh retry
   * budget. Skips the re-queue if an operation with the same ID is already
   * pending, so an interrupted quarantine cannot produce a duplicate write.
   */
  restoreFailedOperation: async (operationId: string): Promise<void> => {
    const failed = await offlineStorage.getFailedOperations();
    const operation = failed.find(op => op.id === operationId);
    if (!operation) return;

    const pending = await offlineStorage.getPendingOperations();
    if (!pending.some(op => op.id === operationId)) {
      // firstDeniedAt goes with them. It is the start of the grace window a
      // rules denial gets before it counts against the retry budget, and by
      // the time someone is looking at this operation on the Dane Offline page
      // that window is long spent — so keeping it would make the very first
      // attempt of a user-initiated retry count as permanent. The case the
      // button exists for is a lapsed subscription just renewed, where the
      // claim needs a moment to propagate: exactly what the window is for.
      const {
        failedAt: _failedAt,
        lastError: _lastError,
        firstDeniedAt: _firstDeniedAt,
        ...rest
      } = operation;
      pending.push({ ...rest, retryCount: 0 });
      await secureStorage.setItem(STORAGE_KEYS.PENDING_OPERATIONS, pending);
    }

    await offlineStorage.removeFailedOperation(operationId);
  },

  restoreAllFailedOperations: async (): Promise<number> => {
    const failed = await offlineStorage.getFailedOperations();
    for (const operation of failed) {
      await offlineStorage.restoreFailedOperation(operation.id);
    }
    return failed.length;
  },

  removeFailedOperation: async (operationId: string): Promise<void> => {
    const failed = await offlineStorage.getFailedOperations();
    await secureStorage.setItem(
      STORAGE_KEYS.FAILED_OPERATIONS,
      failed.filter(op => op.id !== operationId)
    );
  },

  // Offline Client Management
  addPendingClient: async (
    clientData: Omit<PendingClient, 'tempId'>
  ): Promise<string> => {
    const tempId =
      'temp_client_' +
      Date.now().toString() +
      Math.random().toString(36).substr(2, 9);
    const pendingClient: PendingClient = {
      ...clientData,
      tempId,
    };

    // Add to local cache immediately for UI responsiveness
    const cachedClients = await offlineStorage.getCachedClients();
    const clientWithTempId = { ...pendingClient, id: tempId };
    cachedClients.unshift(clientWithTempId);
    await offlineStorage.cacheClients(cachedClients);

    // Add to pending operations queue
    await offlineStorage.addPendingOperation({
      type: 'CREATE_CLIENT',
      data: pendingClient,
    });

    return tempId;
  },

  // Offline Receipt Management
  addPendingReceipt: async (
    receiptData: Omit<PendingReceipt, 'tempId'>
  ): Promise<string> => {
    const tempId =
      'temp_receipt_' +
      Date.now().toString() +
      Math.random().toString(36).substr(2, 9);
    const pendingReceipt: PendingReceipt = {
      ...receiptData,
      tempId,
    };

    // Add to local cache immediately for UI responsiveness
    const cachedReceipts = await offlineStorage.getCachedReceipts();
    const receiptWithTempId = { ...pendingReceipt, id: tempId };
    cachedReceipts.unshift(receiptWithTempId);
    await offlineStorage.cacheReceipts(cachedReceipts);

    // Add to pending operations queue
    await offlineStorage.addPendingOperation({
      type: 'CREATE_RECEIPT',
      data: pendingReceipt,
    });

    return tempId;
  },

  // Sync status (non-encrypted)
  getLastSyncTime: (): number | null => {
    const lastSync = localStorage.getItem(STORAGE_KEYS.LAST_SYNC);
    return lastSync ? parseInt(lastSync, 10) : null;
  },

  // Check if cached data is fresh (less than 24 hours old)
  isCacheFresh: (): boolean => {
    const lastSync = offlineStorage.getLastSyncTime();
    if (!lastSync) return false;

    const oneDay = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    return Date.now() - lastSync < oneDay;
  },

  // Clear all cached data
  clearCache: async (): Promise<void> => {
    await Promise.all(
      DATA_KEYS.map(key =>
        kvRemove(key).catch(error => {
          logger.warn(`Failed to clear offline data key: ${key}`, undefined, {
            component: 'OfflineStorage',
            operation: 'clearCache',
            extra: {
              storageKey: key,
              error: error instanceof Error ? error.message : String(error),
            },
          });
        })
      )
    );
    // Also remove any legacy localStorage copies and the sync timestamp
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  },

  // Get cache info for debugging
  getCacheInfo: async () => {
    const lastSync = offlineStorage.getLastSyncTime();
    const clients = await offlineStorage.getCachedClients();
    const receipts = await offlineStorage.getCachedReceipts();
    const companyDetails = await offlineStorage.getCachedCompanyDetails();
    const products = await offlineStorage.getCachedProducts();
    const categories = await offlineStorage.getCachedCategories();
    const pendingOperations = await offlineStorage.getPendingOperations();
    const failedOperations = await offlineStorage.getFailedOperations();

    return {
      lastSync: lastSync ? new Date(lastSync).toLocaleString('pl-PL') : 'Never',
      isFresh: offlineStorage.isCacheFresh(),
      clientsCount: clients.length,
      receiptsCount: receipts.length,
      productsCount: products.length,
      categoriesCount: categories.length,
      pendingOperationsCount: pendingOperations.length,
      failedOperationsCount: failedOperations.length,
      hasCompanyDetails: !!companyDetails,
      isEncrypted: isEncryptionAvailable(),
      storageSize: new Blob(
        (await Promise.all(DATA_KEYS.map(key => kvGet(key)))).map(
          value => value || ''
        )
      ).size,
    };
  },
};

// Re-export so callers can distinguish quota failures from other errors
export { StorageQuotaError } from './idbKeyValStore';

// NEW: Improved deduplication utility to prevent duplicates by both ID and business logic
const deduplicateAndCleanEntries = <T extends { id: string }>(
  entries: T[],
  tempPrefix: string
): T[] => {
  const seenIds = new Set<string>();
  const seenBusinessKeys = new Set<string>();
  const result: T[] = [];

  // Sort entries to prioritize real entries over temp entries
  const sortedEntries = [...entries].sort((a, b) => {
    const aIsTemp = a.id.startsWith(tempPrefix);
    const bIsTemp = b.id.startsWith(tempPrefix);

    // Real entries come first
    if (!aIsTemp && bIsTemp) return -1;
    if (aIsTemp && !bIsTemp) return 1;
    return 0;
  });

  for (const entry of sortedEntries) {
    // Skip if we've already seen this exact ID
    if (seenIds.has(entry.id)) {
      logger.debug(`Skipping duplicate ID: ${entry.id}`, undefined, {
        component: 'OfflineStorage',
        operation: 'deduplicateAndCleanEntries',
        extra: { duplicateId: entry.id },
      });
      continue;
    }

    // Create business key for deduplication
    let businessKey = '';

    // For clients: use name + address combination
    if ('name' in entry && 'address' in entry) {
      const client = entry as any;
      businessKey =
        `client:${client.name}:${client.address}:${client.documentNumber || ''}`.toLowerCase();
    }

    // For receipts: use number + date + clientId combination
    if ('number' in entry && 'date' in entry && 'clientId' in entry) {
      const receipt = entry as any;
      const dateStr =
        receipt.date instanceof Date
          ? receipt.date.toISOString().split('T')[0]
          : new Date(receipt.date).toISOString().split('T')[0];
      businessKey =
        `receipt:${receipt.number}:${dateStr}:${receipt.clientId}`.toLowerCase();
    }

    // Skip if we've seen this business combination before
    if (businessKey && seenBusinessKeys.has(businessKey)) {
      // logger.debug(
      //   `Skipping duplicate business key: ${businessKey} (ID: ${entry.id})`,
      //   { component: 'offlineStorage', operation: 'deduplicate' }
      // );
      continue;
    }

    // Add to results
    seenIds.add(entry.id);
    if (businessKey) {
      seenBusinessKeys.add(businessKey);
    }
    result.push(entry);
  }

  if (result.length !== entries.length) {
    logger.debug(
      `Deduplication: ${entries.length} → ${result.length} entries`,
      undefined,
      {
        component: 'OfflineStorage',
        operation: 'deduplicateAndCleanEntries',
        extra: { original: entries.length, deduplicated: result.length },
      }
    );
  }

  return result;
};
